/**
 * `<OnvifAuthPrompt>` — bulk-add credentials prompt (ONVIF + Iris-enabled).
 *
 * Per Multi-Add Phase 1 manual QA — bulk-add ONVIF flow:
 * during the bulk-commit pipeline, when an ONVIF probe fails for a
 * source whose discovery didn't carry credentials, the pipeline pauses
 * and signals the UI via `source.bulkAddStatus.kind === 'awaiting-auth'`.
 * The scaffold renders this prompt on top of the bulk progress overlay,
 * blocking the rest of the pipeline until the user either submits
 * credentials (Continue → pipeline retries the probe with the typed
 * creds; if THAT probe also fails the device is auto-skipped) or
 * chooses Skip (the device is excluded and the pipeline moves on).
 *
 * Visual reference: the single-add `<AddSourceAuth>` modal at
 * `add/source/add-source-auth.tsx`. We deliberately reuse the same
 * Dialog primitives + `<_Credentials>` input pair so the bulk prompt
 * feels native to the existing auth UX — the only additions vs
 * single-add are:
 *
 *   - The device name is interpolated into the header (`{name} needs
 *     authentication`) so the user knows which camera is paused.
 *   - The "back" footer slot is a `Skip` button with an explicit
 *     label (`Skip — don't add this source`) to make the consequence
 *     unambiguous.
 *
 * The `kind` discriminator (§2.8): the prompt is reused for BOTH ONVIF
 * probes and Iris-enabled device links (see wiring.ts ~line 515 —
 * Iris-enabled discoveries always-prompt). Prior to this change the
 * caveat copy read "Enter the credentials for this ONVIF camera…",
 * which is wrong for Iris-enabled devices. Copy is discriminated via
 * `kind: 'onvif' | 'iris-enabled'` (default `'onvif'` for backward
 * compat with existing call sites and tests):
 *
 *   - `kind === 'onvif'`        — "Enter the credentials for this
 *     ONVIF camera. These are the same credentials you use to access
 *     the local IP web interface for your device." (Unchanged from
 *     Multi-Add Phase 1.)
 *   - `kind === 'iris-enabled'` — "Enter the credentials for this
 *     Iris-enabled device. Use the same credentials that pair the
 *     device with this studio." Generic wording — no "ONVIF camera"
 *     mention. Copy strings must not reference "ONVIF camera" when
 *     the prompt is rendered for an Iris-enabled link operation.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global): the
 * pause-resume contract is sealed by the `resolve` callback on the
 * `awaiting-auth` BulkAddStatus payload. The prompt invokes resolve
 * with either `{ credentials }` (Continue) or `{ skip: true }`
 * (Skip), and the pipeline resumes on the next event-loop tick.
 *
 * Per `think-in-systems` (ai.knowledge/global): the prompt is a thin
 * UX layer over the discriminated union — no orchestration logic
 * lives here. wiring's `runBulkCommit` owns the retry / skip
 * branching; this component is just data entry.
 */
import * as _Footer from '#~/common/editor/footer/index.js';
import { Credentials as _Credentials, Caveat } from '#~/common/editor/index.js';
// Emit `enter_onvif_password` when the user submits credentials via
// the bulk-add prompt so the analytics funnel records the credential-
// entry interaction (mirroring single-add's existing emit at
// `add-source-auth.tsx`). The probe_outcome enum is emitted downstream
// in `runBulkCommit` after the retry probe resolves; this emit only
// captures the user-visible click action.
// Per `reference-rules-in-comments` (ai.knowledge/global).
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { track } from '#~/analytics/utils/debounced-track.js';
import * as Dialog from '@studio/design-system/dialog';
import type { Credentials as M_Credentials } from '@studio/models/auth';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import { useState } from 'react';

/**
 * Decision the prompt returns through the pipeline's resolve callback.
 * Mirrors the awaiting-auth payload shape in `store/types.ts`.
 */
export type OnvifAuthDecision =
  | { credentials: { username: string; password: string } }
  | { skip: true };

/**
 * Discriminator for the prompt copy strings (§2.8).
 *
 *   onvif        — ONVIF camera credential prompt (default, backward-compatible)
 *   iris-enabled — Iris-enabled device link prompt (generic copy, no "ONVIF")
 *
 * Adding a new kind MUST add a matching `captionForKind` branch below.
 */
export type OnvifAuthPromptKind = 'onvif' | 'iris-enabled';

export interface OnvifAuthPromptProps {
  /** Device name displayed in the header — "{name} needs authentication". */
  name: string;
  /**
   * 1-based index within the batch. Surfaced as "(i/N)" so the user has
   * progress context when several sources need credentials.
   */
  index: number;
  /** Batch size for the same progress context. */
  total: number;
  /** Decision callback the pipeline passed via `bulkAddStatus.resolve`. */
  onDecision: (decision: OnvifAuthDecision) => void;
  /**
   * Prompt copy discriminator (§2.8). Defaults to `'onvif'` so the
   * Multi-Add Phase 1 ONVIF call site (scaffold.tsx) and all existing
   * tests keep their original copy strings without modification.
   * Iris-enabled call sites (always-prompt branch in wiring.ts) pass
   * `'iris-enabled'` to swap the caveat copy.
   */
  kind?: OnvifAuthPromptKind;
}

/**
 * Resolve the caveat copy string per `kind`. Kept as a module-local
 * pure function so the test can import it directly and assert each
 * kind's exact string without rendering the full Dialog tree.
 *
 * `kind === 'iris-enabled'` must NOT mention "ONVIF camera" (§2.8).
 * The Iris-enabled string therefore uses
 * "Iris-enabled device" + a paired-credentials hint matching the
 * `linkIrisDevice` mental model (the device-login API used for the
 * link uses the same credentials the user typed when pairing the
 * device with their studio).
 */
export const captionForKind = (kind: OnvifAuthPromptKind): string => {
  switch (kind) {
    case 'iris-enabled':
      return (
        'Enter the credentials for this Iris-enabled device. ' +
        'Use the same credentials that pair the device with this studio.'
      );
    case 'onvif':
    default:
      return (
        'Enter the credentials for this ONVIF camera. ' +
        'These are the same credentials you use to access the local IP web ' +
        'interface for your device.'
      );
  }
};

/**
 * Render the prompt as a Dialog nested above the bulk-add modal.
 * Returns when the user makes a decision; the parent removes the
 * prompt by transitioning `bulkAddStatus` to `configuring` (retry)
 * or to the next source in the loop.
 */
export const OnvifAuthPrompt = ({
  name,
  index,
  total,
  onDecision,
  // Default `'onvif'` keeps the legacy call sites (scaffold.tsx,
  // existing tests) unchanged. Iris-enabled call sites override to
  // `'iris-enabled'`.
  kind = 'onvif'
}: OnvifAuthPromptProps): ReactElement => {
  const [creds, setCreds] = useState<M_Credentials>();

  // Continue → only valid when both fields are filled; the footer's
  // Next button is disabled otherwise.
  // Fire `enter_onvif_password` mirroring single-add. The
  // probe_outcome (success_after_prompt / skipped_after_retry) is
  // emitted by `runBulkCommit` after the retry probe resolves; this
  // emit only records the user-visible "Continue" click.
  const handleContinue = useLatestFn(() => {
    if (creds?.username && creds?.password) {
      void track(EVENT_NAMES.ENTER_ONVIF_PASSWORD, EVENT_CATEGORIES.VIDEO_SOURCES, {
        deviceName: name
      });
      onDecision({
        credentials: { username: creds.username, password: creds.password }
      });
    }
  });

  // Skip → unambiguous: the source is dropped from the batch. The
  // pipeline counts it as skipped and reports it in the final toast.
  const handleSkip = useLatestFn(() => {
    onDecision({ skip: true });
  });

  return (
    <Dialog.Dialog open onOpenChange={() => undefined}>
      <Dialog.Overlay>
        <Dialog.Content className='fixed left-1/2 top-1/2 flex min-h-[500px] w-[506px] -translate-x-1/2 -translate-y-1/2 flex-col rounded-12 bg-base-200 shadow-lg'>
          <Dialog.Header
            // Interpolating the device name + IP into the title is the
            // primary differentiator vs single-add — the user is on a
            // batch flow and the prompt needs to disambiguate which
            // camera is paused. Producers often have multiple cameras
            // of the same model, so the IP (already in `name`) is
            // load-bearing and must NOT be truncated. Instead the title
            // is rendered at `text-lg` (vs the Dialog default `text-xl`)
            // so the line wraps at most twice for typical device names
            // and the title block doesn't dominate the modal.
            // Per `stay-in-scope` (ai.knowledge/global): per-call
            // override beats a global Dialog Title size change.
            title={
              <Dialog.Title className='text-lg'>
                {`${name} needs authentication (${index}/${total})`}
              </Dialog.Title>
            }
            className='mb-20'
          />
          <div className='flex flex-grow flex-col gap-20 px-40 py-20'>
            {/* Caveat copy discriminated by `kind`. The
                `'iris-enabled'` branch uses generic Iris-paired-creds
                wording and explicitly avoids any "ONVIF camera"
                mention (acceptance criterion). The `'onvif'` branch
                preserves the legacy string. */}
            <Caveat title={captionForKind(kind)} className='mx-auto w-full max-w-[348px]' />
            <div className='space-y-24'>
              <_Credentials credentials={toAccessor(creds, setCreds)} />
            </div>
          </div>
          <Dialog.Footer>
            {/* Skip with explicit consequence — Per user direction:
                make it obvious the source won't be added if the user
                hits this button. */}
            <_Footer.Back label="Skip — don't add this source" onClick={handleSkip} />
            <_Footer.Next
              label='Continue'
              disabled={!creds?.username || !creds?.password}
              onClick={handleContinue}
            />
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog.Dialog>
  );
};
